import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { SiteId, SspId, SsuId } from '@cguard/types';
import {
  getGetV1SitesIdQueryKey,
  getGetV1SitesQueryKey,
  useGetV1SitesId,
  type getV1SitesIdResponse,
} from '../generated/endpoints/sites/sites';
import { cguardFetcher, fetchAllPagedItems } from '../runtime/fetcher';
import {
  normaliseSsuPage,
  normaliseSsuSummary,
  type SsuOnboardingInput,
  type SsuSummary,
} from '../tenants';
import type { ListSitesParams, SiteSummary } from '../view-models';

export type { SsuOnboardingInput, SsuSummary } from '../tenants';

interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

export type BrandedSiteSummary = Omit<SiteSummary, 'id'> & {
  id: SiteId;
  networkIntegration: NetworkIntegrationDesignation;
  owningSsuId?: SsuId;
  owningSspId?: SspId;
  addressLine1?: string;
  addressLine2?: string | null;
  emirate?: string;
  latitude?: number;
  longitude?: number;
  tier?: 'Commercial' | 'HighRisk';
  billingTier?: 'Tier1' | 'Tier2' | 'Tier3';
  /**
   * ADR-0037 amendment 2026-05-09 — per-site enabled audio analytics classes.
   * Optional on the wrapper type while the orval-generated model still tracks
   * the older OpenAPI stub; the central API always emits this field.
   */
  audioEnabledClasses?: readonly string[];
};

export type NetworkIntegrationDesignation =
  | {
      readonly kind: 'legacy-unavailable';
    }
  | {
      readonly kind: 'cguard-owned';
      readonly mode: 'CGuardOwned';
    }
  | {
      readonly kind: 'non-invasive';
      readonly mode: 'NonInvasive';
      readonly designationId: string;
      readonly designationVersion: number;
      readonly designatedByUserId: string;
      readonly designatedAtUtc: string;
    };

export type NonInvasiveNetworkDesignation = Extract<
  NetworkIntegrationDesignation,
  { readonly kind: 'non-invasive' }
>;

type SiteSummaryWire = Partial<SiteSummary> & {
  siteId?: string | null;
  owningSsuId?: string | null;
  owningSspId?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  emirate?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  tier?: string | null;
  billingTier?: string | null;
  audioEnabledClasses?: unknown;
  networkIntegrationMode?: unknown;
  networkIntegrationDesignationId?: unknown;
  networkIntegrationDesignationVersion?: unknown;
  networkIntegrationDesignatedByUserId?: unknown;
  networkIntegrationDesignatedAtUtc?: unknown;
};

export interface SiteProvisioningInput {
  idempotencyKey: string;
  owningSsuId: SsuId;
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  emirate: 'AbuDhabi' | 'Dubai' | 'Sharjah' | 'Ajman' | 'UmmAlQuwain' | 'RasAlKhaimah' | 'Fujairah';
  latitude: number;
  longitude: number;
  tier: 'Commercial' | 'HighRisk';
  billingTier: 'Tier1' | 'Tier2' | 'Tier3';
}

/**
 * Normalize both known site wire contracts:
 *
 * - MSW / stale OpenAPI stub: `{ id, name, city, status }`.
 * - Live central `SiteResponse`: `{ siteId, owningSsuId, ..., status }`.
 */
export function normaliseSiteSummary(value: unknown): BrandedSiteSummary {
  const row = asRecord(value, 'site');
  const wire = row as SiteSummaryWire;
  const id = (wire.id ?? wire.siteId ?? '') as SiteId;
  const audioEnabledClasses = Array.isArray(wire.audioEnabledClasses)
    ? wire.audioEnabledClasses.filter((item): item is string => typeof item === 'string')
    : undefined;

  return {
    id,
    name: typeof wire.name === 'string' ? wire.name : '',
    city: typeof wire.city === 'string' ? wire.city : '',
    status: normaliseSiteStatus(wire.status),
    cameras: finiteNumber(wire.cameras, 0),
    incidents24h: finiteNumber(wire.incidents24h, 0),
    edgeOnline: wire.edgeOnline === true,
    networkIntegration: normaliseNetworkIntegrationDesignation(wire),
    ...(typeof wire.owningSsuId === 'string' ? { owningSsuId: wire.owningSsuId as SsuId } : {}),
    ...(typeof wire.owningSspId === 'string' ? { owningSspId: wire.owningSspId as SspId } : {}),
    ...(typeof wire.addressLine1 === 'string' ? { addressLine1: wire.addressLine1 } : {}),
    ...(wire.addressLine2 === null || typeof wire.addressLine2 === 'string'
      ? { addressLine2: wire.addressLine2 }
      : {}),
    ...(typeof wire.emirate === 'string' ? { emirate: wire.emirate } : {}),
    ...(typeof wire.latitude === 'number' ? { latitude: wire.latitude } : {}),
    ...(typeof wire.longitude === 'number' ? { longitude: wire.longitude } : {}),
    ...(wire.tier === 'Commercial' || wire.tier === 'HighRisk' ? { tier: wire.tier } : {}),
    ...(wire.billingTier === 'Tier1' || wire.billingTier === 'Tier2' || wire.billingTier === 'Tier3'
      ? { billingTier: wire.billingTier }
      : {}),
    ...(audioEnabledClasses ? { audioEnabledClasses } : {}),
  };
}

function unwrapSiteList(body: unknown): BrandedSiteSummary[] {
  if (Array.isArray(body)) return body.map(normaliseSiteSummary);
  const envelope = asRecord(body, 'site list');
  return Array.isArray(envelope.items) ? envelope.items.map(normaliseSiteSummary) : [];
}

const unwrapDetail = (res: getV1SitesIdResponse): BrandedSiteSummary =>
  normaliseSiteSummary((res as { data?: unknown }).data);

export function useSiteList(ssuId?: SsuId): UseQueryResult<BrandedSiteSummary[], Error> {
  const params: ListSitesParams = {};
  if (ssuId) params.ssuId = ssuId as string;
  const query = ssuId ? `?owningSsuId=${encodeURIComponent(ssuId as string)}` : '';
  return useQuery({
    queryKey: [...getGetV1SitesQueryKey(), params] as const,
    queryFn: async () => {
      const response = await cguardFetcher<ApiResponse<unknown>>(`/v1/sites/${query}`, {
        method: 'GET',
      });
      return unwrapSiteList(response.data);
    },
  });
}

export function useAllSiteList({
  enabled = true,
}: { readonly enabled?: boolean } = {}): UseQueryResult<BrandedSiteSummary[], Error> {
  return useQuery({
    queryKey: [...getGetV1SitesQueryKey(), 'all-pages'] as const,
    enabled,
    queryFn: () =>
      fetchAllPagedItems(async (page, pageSize) => {
        const response = await cguardFetcher<ApiResponse<unknown>>(
          `/v1/sites?page=${page}&pageSize=${pageSize}`,
          { method: 'GET' },
        );
        return response.data;
      }, normaliseSiteSummary),
  });
}

export function useSiteDetail(id: SiteId | null): UseQueryResult<BrandedSiteSummary, Error> {
  return useGetV1SitesId<BrandedSiteSummary, Error>((id ?? '') as string, {
    query: { enabled: Boolean(id), select: unwrapDetail },
  });
}

export function useDesignateNonInvasiveNetwork(): UseMutationResult<
  NonInvasiveNetworkDesignation,
  Error,
  { readonly siteId: SiteId }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ siteId }) => {
      const response = await cguardFetcher<ApiResponse<unknown>>(
        `/v1/sites/${encodeURIComponent(String(siteId))}/network-integration-mode`,
        {
          method: 'PUT',
          body: JSON.stringify({ mode: 'NonInvasive' }),
        },
      );
      return parseNonInvasiveNetworkDesignation(response.data, String(siteId));
    },
    onSuccess: async (_designation, { siteId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetV1SitesIdQueryKey(String(siteId)),
        }),
        queryClient.invalidateQueries({ queryKey: getGetV1SitesQueryKey() }),
      ]);
    },
  });
}

export function useCreateSite(): UseMutationResult<
  BrandedSiteSummary,
  Error,
  SiteProvisioningInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const response = await cguardFetcher<ApiResponse<unknown>>('/v1/sites/', {
        method: 'POST',
        body: JSON.stringify({
          ...input,
          addressLine2: emptyToNull(input.addressLine2),
        }),
      });
      return normaliseSiteSummary(response.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getGetV1SitesQueryKey() });
    },
  });
}

export function useSsuList(owningSspId?: SspId): UseQueryResult<SsuSummary[], Error> {
  const query = owningSspId ? `?owningSspId=${encodeURIComponent(owningSspId as string)}` : '';
  return useQuery({
    queryKey: ['/v1/tenants/ssus', owningSspId ?? null],
    queryFn: async () => {
      const response = await cguardFetcher<ApiResponse<unknown>>(`/v1/tenants/ssus/${query}`, {
        method: 'GET',
      });
      return normaliseSsuPage(response.data);
    },
  });
}

export function useCreateSsu(): UseMutationResult<SsuSummary, Error, SsuOnboardingInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const response = await cguardFetcher<ApiResponse<unknown>>('/v1/tenants/ssus/', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return normaliseSsuSummary(response.data);
    },
    onSuccess: async (_data, input) => {
      await queryClient.invalidateQueries({
        queryKey: ['/v1/tenants/ssus', input.owningSspId],
      });
      await queryClient.invalidateQueries({ queryKey: ['/v1/tenants/ssus'] });
    },
  });
}

/**
 * Canonical YAMNet+MLP audio class catalog (ADR-0037 amendment 2026-05-09).
 * Mirrors `services/central/src/Modules/Sites/Domain/Sites/AudioClassCatalog.cs`
 * so the operator console's chip-multiselect renders the full set without
 * having to round-trip through the API.
 */
export const AUDIO_CLASS_CATALOG = [
  'gunshot',
  'glass_break',
  'scream',
  'raised_voices',
  'alarm_bell',
  'crash_impact',
] as const;

export type AudioClass = (typeof AUDIO_CLASS_CATALOG)[number];

/** Default-enabled subset auto-applied on SIRA opt-in per Phase E4. */
export const AUDIO_CLASS_DEFAULT_SUBSET: readonly AudioClass[] = [
  'gunshot',
  'glass_break',
  'scream',
] as const;

/**
 * PATCH /v1/sites/{id}/audio-classes — replace the per-site enabled audio
 * analytics class set. Empty array permitted (operator opts out of every
 * class — surfaced as a deviation in the daily audit-log export).
 */
export function useUpdateSiteAudioClasses(): UseMutationResult<
  void,
  Error,
  { id: SiteId; classes: readonly string[] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, classes }) => {
      await cguardFetcher<void>(`/v1/sites/${id as string}/audio-classes`, {
        method: 'PATCH',
        body: JSON.stringify({ classes }),
      });
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: getGetV1SitesIdQueryKey(vars.id as string) });
      void qc.invalidateQueries({ queryKey: getGetV1SitesQueryKey() });
    },
  });
}

function normaliseSiteStatus(value: unknown): SiteSummary['status'] {
  switch (
    String(value ?? '')
      .trim()
      .toLowerCase()
  ) {
    case 'live':
    case 'active':
      return 'active';
    case 'decommissioned':
      return 'decommissioned';
    case 'decommissioning':
      return 'decommissioning';
    case 'suspended':
    case 'maintenance':
      return 'suspended';
    default:
      return 'provisioning';
  }
}

function normaliseNetworkIntegrationDesignation(
  wire: SiteSummaryWire,
): NetworkIntegrationDesignation {
  if (wire.networkIntegrationMode === undefined || wire.networkIntegrationMode === null) {
    return { kind: 'legacy-unavailable' };
  }
  if (wire.networkIntegrationMode === 'CGuardOwned') {
    const designationMetadata = [
      wire.networkIntegrationDesignationId,
      wire.networkIntegrationDesignationVersion,
      wire.networkIntegrationDesignatedByUserId,
      wire.networkIntegrationDesignatedAtUtc,
    ];
    if (designationMetadata.some((value) => value !== undefined && value !== null)) {
      throw new Error('CGuard-owned site unexpectedly carries network designation metadata.');
    }
    return { kind: 'cguard-owned', mode: 'CGuardOwned' };
  }
  if (wire.networkIntegrationMode !== 'NonInvasive') {
    throw new Error('Site network integration mode is invalid.');
  }
  return parseNonInvasiveNetworkDesignation({
    siteId: wire.siteId ?? wire.id,
    mode: wire.networkIntegrationMode,
    designationId: wire.networkIntegrationDesignationId,
    designationVersion: wire.networkIntegrationDesignationVersion,
    designatedByUserId: wire.networkIntegrationDesignatedByUserId,
    designatedAtUtc: wire.networkIntegrationDesignatedAtUtc,
  });
}

function parseNonInvasiveNetworkDesignation(
  value: unknown,
  expectedSiteId?: string,
): NonInvasiveNetworkDesignation {
  const row = asRecord(value, 'network integration designation');
  if (expectedSiteId !== undefined && row.siteId !== expectedSiteId) {
    throw new Error('Central confirmed a network designation for a different site.');
  }
  if (row.mode !== 'NonInvasive') {
    throw new Error('Central did not confirm the non-invasive network mode.');
  }
  if (
    typeof row.designationVersion !== 'number' ||
    !Number.isSafeInteger(row.designationVersion) ||
    row.designationVersion !== 1
  ) {
    throw new Error('Central returned an invalid network designation version.');
  }
  const designatedAtUtc = requireString(row.designatedAtUtc, 'network designation timestamp');
  if (!Number.isFinite(Date.parse(designatedAtUtc))) {
    throw new Error('Network designation timestamp is invalid.');
  }
  return {
    kind: 'non-invasive',
    mode: 'NonInvasive',
    designationId: requireString(row.designationId, 'network designation id'),
    designationVersion: row.designationVersion,
    designatedByUserId: requireString(row.designatedByUserId, 'network designating operator id'),
    designatedAtUtc,
  };
}

function finiteNumber(value: unknown, fallback: number): number {
  const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof number === 'number' && Number.isFinite(number) ? number : fallback;
}

function asRecord(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${label} response is invalid.`);
  }
  return value as Record<string, unknown>;
}

function requireString(value: unknown, label: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${label} is missing.`);
  }
  return value;
}

function emptyToNull(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}
